import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, CircularProgress, Divider, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../constants";
import ContainedButton from "../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../shared/Button/customOutlinedButton";
import { removeAuditLog } from "../../store/AuditLogSlices/auditLogSlice";
import { setLoginDetails } from "../../store/features/auth/authSlice";
import { removeCreateParticipant } from "../../store/features/auth/createParticpantSlice";
import { setParticipantDetails } from "../../store/features/auth/fundParticipantsSlice";
import { setParticipantAdminProfile } from "../../store/features/auth/participantAdminProfileSlice";
import { removeGlAdjustment } from "../../store/GLAdjustments/createNewGlAdjustmentSlice";
import { setRefreshCountFlag } from "../../store/Invoice/invoiceCountSlice";
import { removeCreateNewLoan } from "../../store/LoansStore/newLoanSlice";
import { setActiveIndex } from "../../store/MenutItems/menuUiSlice";
import { removeNetSuiteLog } from "../../store/NetSuiteSlices/netSuiteLogSlice";
import { removeCreateSpecialFund } from "../../store/SpecialFunds/createNewFundSlice";
import { RootState } from "../../store/store";
import { tablesToReset } from '../../store/tableConstants';
import { resetTableState } from "../../store/tableSlice";
import { getAllParticipants } from "../fiscalUpgrade/services/ParticipantsServices/SwitchParticipartService";
import { getAssetSummaryByPId } from "../fiscalUpgrade/services/Summary/accountSummaryService";
import { userDetailsUpdate } from "../fiscalUpgrade/services/Users/user-Info.service";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

interface propsHeader {
  setSwitchPartcipantFlag: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  titlePopUp: string;
  onDefaultParticipantSelect?: (participantId: any) => void;
  setIsAdminView?: React.Dispatch<React.SetStateAction<boolean>>;
  defaultPId?: number;
}

export default function SwitchParticipant(props: propsHeader) {
  const { setSwitchPartcipantFlag, open, titlePopUp, defaultPId } = props;
  const participantDetail = useSelector((state: RootState) => state.participantDetails);
  const userDetail = useSelector((state: RootState) => state.auth);
  const [switchParticpantId, setSwitchParticpantId] = useState<any>(null);
  const [defaultParticpantId, setDefaultParticpantId] = useState<any>(defaultPId);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const User = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const projectInvoiceRoles = [
    constants.ROLES_PROJECT_MANAGER, 
    constants.ROLES_PROJECT_ANALYST,
  ];
  useEffect(() => {
    if (open) {
      getParticipants();
      titlePopUp === translate('selectParticipantToSwitch') ?
        setSwitchParticpantId(participantDetail?.participantId) :
        setDefaultParticpantId(defaultPId)
    }
  }, [open]);

  async function getParticipants() {
    let participantVO = {
      page: -1,
      size: -1,
      isActive: "Y",
      clientId: User.clientId
    };
    try {
      const promise = await getAllParticipants(participantVO);
      dispatch(setParticipantDetails({ participantsArray: promise.data?.data?.data }));
    } catch (error) {
      console.error(error);
    }
  }

  function handleParticipantChange(event: any,
    newValue: { participantId: any; participantName: string } | null
  ) {
    titlePopUp === translate('selectParticipantToSwitch') ?
      setSwitchParticpantId(newValue ? newValue.participantId : null) :
      setDefaultParticpantId(newValue ? newValue.participantId : null)
  }

  function handleSwitch(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    tablesToReset.forEach(tableKey => { dispatch(resetTableState({ tableKey })) });
    const updatePartcipantId = {
      defaultParticipantId: switchParticpantId
    }
    updateUser(User.id, updatePartcipantId);
    dispatch(removeAuditLog());
    dispatch(removeCreateParticipant());
    dispatch(removeCreateSpecialFund());
    dispatch(removeNetSuiteLog());
    dispatch(removeCreateNewLoan());
    dispatch(removeGlAdjustment());
    handleClose(e);
    
  }
  function handleDefaultParticipant(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (props.onDefaultParticipantSelect) {
      props.onDefaultParticipantSelect(defaultParticpantId);
    }
    setSwitchPartcipantFlag(false);
  }
  async function updateUser(userId: number, userInfoUpdate: any) {
    setLoadingFlag(true);
    try {
      const userUpdateRes: any = await userDetailsUpdate(userId, userInfoUpdate);
      if (userUpdateRes.status === constants.STATUS_CODES.SUCCESS) {
        dispatch(setParticipantDetails({ participantId: switchParticpantId }));
        dispatch(setLoginDetails({ defaultParticipantId: switchParticpantId }));
        const participant = (participantDetail.participantsArray as { participantId: string; participantName: string }[]).find((item) => item.participantId === switchParticpantId);
        dispatch(setParticipantAdminProfile({ participantAdminProfile: participant?.participantName || '' }));
        dispatch(setActiveIndex(0));
        dispatch(setRefreshCountFlag(true));
        const resAssetByPid = await getAssetSummaryByPId(switchParticpantId, userDetail.clientId);
        if (resAssetByPid.status === constants.STATUS_CODES.SUCCESS) {
          if (resAssetByPid && resAssetByPid.data.data.cashAccounts
            && resAssetByPid.data.data.cashAccounts.availableBalance < 0) {
            const message = `Participant has a checking balance overdraft of $${resAssetByPid.data.data.cashAccounts.availableBalance}`;
            notifyUser(message, 'warning');
          }
        }
        if (projectInvoiceRoles.includes(User?.roles?.roleName ?? ""))
          navigate("/vendorInvoiceSummary");
        else
          navigate("/summary");
        props.setIsAdminView?.(false);
        setLoadingFlag(false);
      } else {
        notifyUser(userUpdateRes?.response?.data?.message, "error", translate("switchParticipant"));
        setLoadingFlag(false);
      }
    } catch (error) {
      console.warn(error);
      setLoadingFlag(false);
    }
  }
  function handleClose(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setSwitchPartcipantFlag(false);
  }

  return (
    <>
      <Dialog
        open={open}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            background: "white",
            width: "25%",
            maxWidth: "25%",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#059ca9",
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 10px',
          }}
        >
          {titlePopUp}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: "white", padding: '0px', ml: 6 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
          <DialogContentText
            sx={{
              whiteSpace: 'normal', // allow wrapping
              overflowWrap: 'break-word', // break long words
              wordBreak: 'break-word', // ensure long words wrap
              fontSize: '1rem'
            }}>
            <Autocomplete
              options={participantDetail.participantsArray}
              getOptionLabel={(option: any) => option.participantName || ""}
              onChange={handleParticipantChange}
              value={
                participantDetail.participantsArray?.find((p: any) =>
                  titlePopUp === translate("selectParticipantToSwitch")
                    ? p.participantId === switchParticpantId
                    : p.participantId === defaultParticpantId
                ) || null
              }
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" }, // Ensures the dropdown background is white
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate("selectParticipant")}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions>
          {titlePopUp === translate('selectParticipantToSwitch') ?
            <ContainedButton variant="contained" onClick={handleSwitch} >
              {translate('continue')}
            </ContainedButton> :
            <ContainedButton variant="contained" onClick={handleDefaultParticipant}>
              {translate('select')}
            </ContainedButton>}
          <OutlinedButton variant="outlined" onClick={handleClose}>{translate('cancel')}</OutlinedButton>
        </DialogActions>
      </Dialog>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
        }}
        open={loadingFlag}
      >
        <CircularProgress color="inherit" />
        <Typography color={"white"}>{translate('pleasewait')}</Typography>
      </Backdrop>
    </>
  );
}
